import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { RadioGroupProps as AriaRadioGroupProps } from 'react-aria-components/RadioGroup';
import { RadioGroup as AriaRadioGroup } from 'react-aria-components/RadioGroup';
import type { ContextValue } from 'react-aria-components/slots';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/RadioGroup.module.css';

const radioGroupStyles = cva(styles.group);

interface RadioGroupProps extends AriaRadioGroupProps {
	ref?: Ref<HTMLDivElement>;
}

const RadioGroupContext = createContext<ContextValue<RadioGroupProps, HTMLDivElement>>(null);

/**
 * A radio group allows a user to select a single item from a list of mutually exclusive options.
 *
 * https://react-spectrum.adobe.com/react-aria/RadioGroup.html
 */
const RadioGroup = ({ ref, ...props }: RadioGroupProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, RadioGroupContext);
	return (
		<AriaRadioGroup
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				radioGroupStyles({ ...renderProps, className }),
			)}
		/>
	);
};

export { RadioGroup, RadioGroupContext, radioGroupStyles };
export type { RadioGroupProps };
